import { Spacing } from '@persona-id/design-tokens';
import styled from 'styled-components';

import Divider from 'dashboard/components/Divider';
import ListMatchEntity from 'dashboard/components/ReportResult/ListMatchEntity';
import { PersonaWatchlistMatchedEntities } from 'dashboard/components/ReportResult/Watchlist';
import { RelatedSource } from 'dashboard/models/report/adverse-media';
import { WatchlistMatch } from 'dashboard/models/report/watchlist';
import Button from 'design-system/components/Button';
import Sticker from 'design-system/components/Sticker';
import { headingMixins } from 'design-system/mixins/text';
import { ApiModel } from 'lib/interfaces/api';

import { AdverseMediaMediaList } from '../ReportResult/AdverseMediaIdentityMatchDetails';

const SubTitle = styled.div`
  ${headingMixins.sm};
  font-weight: 500;
`;

const StyledList = styled.div`
  padding: ${Spacing[4]};
  margin: ${Spacing[2]} 0px;
  border-width: 1px;
  border-style: solid;
  border-color: ${({ theme }) => theme.color.border.secondary};
  border-radius: 1px;
`;

const Footer = styled.div`
  padding-top: ${Spacing[4]};
  margin-top: ${Spacing[4]};
  margin-bottom: -${Spacing[5]};
`;

interface WatchlistSnapshotMatchesProps {
  entries: WatchlistMatch[];
  onBackClick: () => void;
}

const WatchlistSnapshotMatches = ({ entries, onBackClick }: WatchlistSnapshotMatchesProps) => {
  if (!entries) {
    return null;
  }

  return (
    <>
      <SubTitle>
        Found Matches <Sticker>{entries.length}</Sticker>
      </SubTitle>
      <StyledList>
        {entries.map((match, index) => {
          return (
            <ListMatchEntity
              key={`${match}-${index}`}
              url={match.url}
              name={match.name}
              flagCode={
                match.slug === 'internal-data-source'
                  ? undefined
                  : match['flag-code'] || match['country-code']
              }
            >
              {match.entities && <PersonaWatchlistMatchedEntities entities={match.entities} />}
            </ListMatchEntity>
          );
        })}
      </StyledList>
      <Footer>
        <Button
          id='report-history-timeline-watchlist-matches__back_button'
          onClick={onBackClick}
          width='auto'
          variant='basic'
        >
          Back
        </Button>
      </Footer>
    </>
  );
};

interface AdverseMediaSnapshotMatchesProps {
  entries: RelatedSource[];
  onBackClick: () => void;
}

const AdverseMediaSnapshotMatches = ({
  entries,
  onBackClick,
}: AdverseMediaSnapshotMatchesProps) => {
  if (!entries) {
    return null;
  }

  return (
    <div>
      {entries.map((entry) => (
        <>
          <SubTitle>{entry['name']}</SubTitle>
          <Divider />
          <SubTitle>
            Found Matches <Sticker>{entry['media']?.length}</Sticker>
          </SubTitle>
          <StyledList>
            {entry['media'] && entry['name'] && entry['akas'] && (
              <AdverseMediaMediaList
                media={entry['media']}
                names={[entry['name']]}
                akas={entry['akas']}
              />
            )}
          </StyledList>
        </>
      ))}
      <Footer>
        <Button
          id='report-history-timeline-am-matches__back_button'
          onClick={onBackClick}
          width='auto'
          variant='basic'
        >
          Back
        </Button>
      </Footer>
    </div>
  );
};

interface ReportSnapshotMatchesProps {
  reportType: string;
  entries: RelatedSource[] | WatchlistMatch[];
  onBackClick: () => void;
}

export const ReportSnapshotMatches = ({
  reportType,
  entries,
  onBackClick,
}: ReportSnapshotMatchesProps) => {
  switch (reportType) {
    case ApiModel.ReportWatchlist:
      return (
        <WatchlistSnapshotMatches entries={entries as WatchlistMatch[]} onBackClick={onBackClick} />
      );
    case ApiModel.ReportAdverseMedia:
      return (
        <AdverseMediaSnapshotMatches
          entries={entries as RelatedSource[]}
          onBackClick={onBackClick}
        />
      );
    default:
      return null;
  }
};
